import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import Soundfont from 'soundfont-player';
import { instrumentConfigs } from '../constants/instrumentConfigs';
import {
    getGridStepBeatsFromGridSize,
    beatToGridIndex,
    gridIndexToBeat,
    getTripletQuantizeSpec,
    assignTripletGroupTargets,
    snapSingleToTripletOnset,
} from '../utils/musicTiming';

// ============================================
// QUANTIZATION UTILITIES
// ============================================

// Quantize division options (in beats)
const QUANTIZE_DIVISIONS = [
    { label: '1/1 Note', value: 4 },        // Whole note = 4 beats
    { label: '1/2 Note', value: 2 },        // Half note = 2 beats
    { label: '1/4 Note', value: 1 },        // Quarter note = 1 beat
    { label: '1/8 Note', value: 0.5 },      // Eighth note = 0.5 beats
    { label: '1/16 Note', value: 0.25 },    // Sixteenth note = 0.25 beats
    { label: '1/32 Note', value: 0.125 },   // 32nd note = 0.125 beats
    { label: '1/64 Note', value: 0.0625 },  // 64th note = 0.0625 beats
    { label: '1/4 Triplet', value: 2 / 3 },
    { label: '1/8 Triplet', value: 1 / 3 },
    { label: '1/16 Triplet', value: 1 / 6 },
    { label: '1/32 Triplet', value: 1 / 12 },
];

// Scale quantize options
const SCALE_QUANTIZE_OPTIONS = [
    { label: 'Off', value: null },
    { label: 'Major (Ionian)', value: [0, 2, 4, 5, 7, 9, 11] },
    { label: 'Minor (Aeolian)', value: [0, 2, 3, 5, 7, 8, 10] },
    { label: 'Dorian', value: [0, 2, 3, 5, 7, 9, 10] },
    { label: 'Phrygian', value: [0, 1, 3, 5, 7, 8, 10] },
    { label: 'Lydian', value: [0, 2, 4, 6, 7, 9, 11] },
    { label: 'Mixolydian', value: [0, 2, 4, 5, 7, 9, 10] },
    { label: 'Pentatonic Maj', value: [0, 2, 4, 7, 9] },
    { label: 'Pentatonic Min', value: [0, 3, 5, 7, 10] },
    { label: 'Blues', value: [0, 3, 5, 6, 7, 10] },
];

/**
 * Classic quantize algorithm - snaps notes to nearest grid point.
 * For triplet steps, single-note snap uses valid triplet partials only (see snapSingleToTripletOnset).
 * Multi-note triplet quantize uses assignTripletGroupTargets in applyQuantize.
 * @param {number} startTime - Note start time in beats
 * @param {number} gridSize - Quantize grid size in beats
 * @param {number} strength - Quantize strength (0-100)
 * @param {number} swing - Swing amount (0-100, 50 = no swing)
 * @returns {number} - Quantized start time
 */
function quantizeNoteTime(startTime, gridSize, strength = 100, swing = 50) {
    const tripletSpec = getTripletQuantizeSpec(gridSize);
    let targetGridTime;
    if (tripletSpec) {
        targetGridTime = snapSingleToTripletOnset(startTime, tripletSpec.periodBeats);
    } else {
        const gridPosition = Math.round(startTime / gridSize);
        targetGridTime = gridPosition * gridSize;
    }

    const gridPosition = tripletSpec
        ? Math.round(targetGridTime / (gridSize || 1e-9))
        : Math.round(startTime / gridSize);

    // Apply swing to every second grid position (odd positions) — not applied to triplet grid
    if (!tripletSpec && gridPosition % 2 === 1 && swing !== 50) {
        // Swing shifts the "off-beat" grid positions
        // swing = 50 means no shift (straight timing)
        // swing = 66 means 2:1 ratio (typical swing)
        // swing = 75 means 3:1 ratio (heavy swing)
        const swingFactor = (swing - 50) / 50; // -1 to 1
        const maxSwingOffset = gridSize * 0.5; // Maximum swing is half a grid
        targetGridTime += swingFactor * maxSwingOffset;
    }

    // Apply strength - interpolate between original and quantized position
    const strengthFactor = strength / 100;
    const quantizedTime = startTime + (targetGridTime - startTime) * strengthFactor;

    return Math.max(0, quantizedTime);
}

/**
 * Quantize a note's pitch to a scale
 * @param {number} midiPitch - MIDI note number
 * @param {number[]} scaleIntervals - Array of semitone intervals from root
 * @param {number} rootNote - Root note MIDI number (default C = 0)
 * @returns {number} - Quantized MIDI pitch
 */
function quantizeNoteToScale(midiPitch, scaleIntervals, rootNote = 0) {
    if (!scaleIntervals || scaleIntervals.length === 0) return midiPitch;

    const noteInOctave = (midiPitch - rootNote) % 12;
    const octave = Math.floor((midiPitch - rootNote) / 12);

    // Find the closest scale degree
    let closestInterval = scaleIntervals[0];
    let minDistance = Math.abs(noteInOctave - closestInterval);

    for (const interval of scaleIntervals) {
        const distance = Math.abs(noteInOctave - interval);
        // Also check wrapping around the octave
        const wrapDistance = Math.abs(noteInOctave - (interval + 12));
        const wrapDistanceNeg = Math.abs(noteInOctave - (interval - 12));

        const effectiveDistance = Math.min(distance, wrapDistance, wrapDistanceNeg);

        if (effectiveDistance < minDistance) {
            minDistance = effectiveDistance;
            closestInterval = interval;
        }
    }

    return rootNote + octave * 12 + closestInterval;
}

// Define the notes from highest to lowest (C8 to C1)
const generateNotes = () => {
    const notes = [];
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

    // Generate from C8 down to C1 (highest to lowest for display)
    // MIDI note numbers: C1=24, C4=60, A4=69, C8=108
    // Limit to C8 (108) as soundfonts typically don't have samples above this
    for (let octave = 8; octave >= 1; octave--) {
        for (let i = noteNames.length - 1; i >= 0; i--) {
            const noteName = noteNames[i];
            const fullNote = `${noteName}${octave}`;
            // Correct MIDI calculation: C4 = 60, so formula is (octave + 1) * 12 + noteIndex
            // But we want C1=24, so: C1 = (1-1)*12 + 0 + 24 = 24
            const midiNumber = octave * 12 + i + 12;

            // Only include notes from C1 (24) to C8 (108)
            // Notes above C8 don't have soundfont samples
            if (midiNumber >= 24 && midiNumber <= 108) {
                notes.push({
                    name: fullNote,
                    midi: midiNumber,
                    isBlack: noteName.includes('#'),
                    octave: octave,
                    noteIndex: i
                });
            }
        }
    }

    return notes;
};

// Utility to convert note name to frequency
function noteToFrequency(note) {
    const A4 = 440;
    const noteOrder = [
        'C', 'C#', 'D', 'D#', 'E', 'F',
        'F#', 'G', 'G#', 'A', 'A#', 'B'
    ];

    const match = note.match(/^([A-G]#?)(\d)$/);
    if (!match) return A4;
    const [, pitch, octaveStr] = match;
    const octave = parseInt(octaveStr);
    const pitchIndex = noteOrder.indexOf(pitch);
    const noteNumber = pitchIndex + (octave + 1) * 12;

    const A4Number = noteOrder.indexOf('A') + 4 * 12 + 12; // MIDI number for A4
    return A4 * Math.pow(2, (noteNumber - A4Number) / 12);
}

// Get color based on velocity (0-1)
// Low velocity (0) = purple (hue 280)
// Middle velocity (0.5) = green (hue 120)  
// High velocity (1) = red (hue 0)
function getVelocityColor(velocity, isSelected = false) {
    // Map velocity to hue: 0 -> 280 (purple), 0.5 -> 120 (green), 1 -> 0 (red)
    let hue;
    if (velocity <= 0.5) {
        // Purple (280) to Green (120)
        hue = 280 - (velocity * 2) * 160; // 280 -> 120
    } else {
        // Green (120) to Red (0)
        hue = 120 - ((velocity - 0.5) * 2) * 120; // 120 -> 0
    }

    const saturation = 70 + (Math.abs(velocity - 0.5) * 30); // More saturated at extremes
    const lightness = isSelected ? 85 : 45;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/** Row keys may contain '#' (e.g. C#4); grid index is always the last -segment. */
function parseGridCellKey(key) {
    const i = key.lastIndexOf('-');
    if (i <= 0) return { rowKey: key, gridIndex: 0 };
    return { rowKey: key.slice(0, i), gridIndex: parseInt(key.slice(i + 1), 10) || 0 };
}

const PianoRoll = ({
    pixelsPerBeat = 50,
    gridSize = 16,
    timeSignature = { numerator: 4, denominator: 4 },
    clipStart = 0,
    clipDuration = 16,
    notes: clipNotes = [],
    selectedNotes = [],
    sourceDuration = 0,    // original content length (beats) — used to render loop ghosts
    clipLoop = false,      // whether this clip is looping
    onNoteClick,
    onNoteContextMenu,
    onNoteMouseDown,
    onGridClick,
    onGridRightClick,
    onPianoKeyClick,
    cursorMode = 'select',
    currentTrack,
    selectedClip,
    onInstrumentChange,
    onNoteUpdate
}) => {
    const audioCtxRef = useRef(null);
    const soundfontInstrumentsRef = useRef({}); // Store multiple instruments
    const soundfontLoadingRef = useRef({});
    const pianoKeysRef = useRef(null);
    const gridRef = useRef(null);
    const velocityLaneRef = useRef(null);
    const [grid, setGrid] = useState({});

    // Velocity lane height
    const VELOCITY_LANE_HEIGHT = 80;

    const onNoteClickRef = useRef(onNoteClick);
    useEffect(() => {
        onNoteClickRef.current = onNoteClick;
    }, [onNoteClick]);

    // Selection rectangle state for drag-to-select
    const [selectionRect, setSelectionRect] = useState(null);
    const [isDraggingSelection, setIsDraggingSelection] = useState(false);
    const dragStartRef = useRef(null);

    // Quantization state
    const [quantizeValue, setQuantizeValue] = useState(0.25); // Default 1/16 note
    const [quantizeStrength, setQuantizeStrength] = useState(100);
    const [quantizeSwing, setQuantizeSwing] = useState(50); // 50 = no swing
    const [scaleQuantize, setScaleQuantize] = useState(null); // Scale intervals or null
    const [scaleRoot, setScaleRoot] = useState(0); // C = 0

    // Store original note positions for non-destructive quantize
    const originalNotePositionsRef = useRef(new Map());

    // Left panel width state (resizable)
    const [leftPanelWidth, setLeftPanelWidth] = useState(160);
    const [isResizingPanel, setIsResizingPanel] = useState(false);
    const resizeStartRef = useRef({ x: 0, width: 0 });

    // Initialize from track's stored instrument or default to piano
    const [previewInstrument, setPreviewInstrument] = useState(
        currentTrack?.soundfontInstrument || 'acoustic_grand_piano'
    );

    const allNotes = generateNotes();

    // Update preview instrument when track changes
    useEffect(() => {
        if (currentTrack?.soundfontInstrument) {
            setPreviewInstrument(currentTrack.soundfontInstrument);
        }
    }, [currentTrack?.soundfontInstrument]);

    // Drums mode detection and rows preparation
    const isDrums = (currentTrack?.instrument === 'drums') || ((currentTrack?.name || '').toLowerCase().includes('drum'));
    const drumRows = React.useMemo(() => {
        const drumMap = instrumentConfigs?.drums?.drumMap || {};
        const order = [36, 38, 42, 46, 49, 51, 45, 47, 43, 57];
        const rows = order.filter(m => drumMap[m]).map(m => ({ midi: m, label: drumMap[m].label, name: drumMap[m].name }));
        Object.keys(drumMap).forEach(k => {
            const midi = Number(k);
            if (!order.includes(midi)) rows.push({ midi, label: drumMap[midi].label, name: drumMap[midi].name });
        });
        return rows;
    }, [currentTrack]);
    const rows = isDrums ? drumRows : allNotes;

    const beatsPerBar = Math.max(1, timeSignature?.numerator ?? 4);
    const gridStepBeats = getGridStepBeatsFromGridSize(gridSize || 16);
    const cellWidth = pixelsPerBeat * gridStepBeats;

    // Piano roll grid: snap subdivision (triplet or straight), beat, bar
    const adaptiveGridCSS = useMemo(() => {
        const barPx = pixelsPerBeat * beatsPerBar;
        const finePx = Math.max(2, pixelsPerBeat * gridStepBeats);
        return {
            backgroundImage: [
                `repeating-linear-gradient(to right, transparent 0px, transparent ${finePx - 1}px, rgba(255,255,255,0.05) ${finePx - 1}px, rgba(255,255,255,0.05) ${finePx}px)`,
                `repeating-linear-gradient(to right, transparent 0px, transparent ${pixelsPerBeat - 1}px, rgba(255,255,255,0.08) ${pixelsPerBeat - 1}px, rgba(255,255,255,0.08) ${pixelsPerBeat}px)`,
                `repeating-linear-gradient(to right, transparent 0px, transparent ${barPx - 1}px, rgba(255,255,255,0.18) ${barPx - 1}px, rgba(255,255,255,0.18) ${barPx}px)`,
            ].join(', '),
            backgroundSize: `${finePx}px 100%, ${pixelsPerBeat}px 100%, ${barPx}px 100%`,
        };
    }, [pixelsPerBeat, beatsPerBar, gridStepBeats]);

    const clipStartBeats = Number.isFinite(clipStart) ? clipStart : 0;
    const clipDurationBeats = Number.isFinite(clipDuration) ? clipDuration : 16;

    // "Unlimited" horizontal bars: start with a reasonable amount and extend as the user scrolls.
    const minBars = 4;
    const baseBars = Math.max(minBars, Math.ceil(clipDurationBeats / beatsPerBar));
    const initialViewBars = Math.max(16, baseBars);
    const [viewBars, setViewBars] = useState(initialViewBars);
    const lastSelectedClipRef = useRef(selectedClip);

    useEffect(() => {
        // Reset the view width when switching clips; otherwise only grow to fit content.
        if (lastSelectedClipRef.current !== selectedClip) {
            lastSelectedClipRef.current = selectedClip;
            setViewBars(initialViewBars);
            return;
        }

        setViewBars(prev => Math.max(prev, baseBars));
    }, [selectedClip, initialViewBars, baseBars]);

    const visibleBeats = viewBars * beatsPerBar;
    const totalSteps = Math.max(1, Math.ceil(visibleBeats / gridStepBeats));
    const gridWidthPx = visibleBeats * pixelsPerBeat;

    const rowsRef = useRef(rows);
    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    // Note dragging state for moving notes
    const [isDraggingNotes, setIsDraggingNotes] = useState(false);
    const noteDragRef = useRef(null);

    // Convert clip notes to grid format with duration information
    useEffect(() => {
        const newGrid = {};
        clipNotes.forEach(note => {
            // Find the row from MIDI number
            const noteObj = rows.find(n => n.midi === note.pitch);
            if (noteObj) {
                const gridIdx = beatToGridIndex(note.start, gridStepBeats);
                const rowKey = isDrums ? `${noteObj.midi}` : noteObj.name;
                const key = `${rowKey}-${gridIdx}`;
                newGrid[key] = {
                    active: true,
                    noteId: note.id,
                    duration: note.duration,
                    velocity: note.velocity,
                    originalNote: note
                };

                const durationInGridUnits = Math.max(1, Math.round((note.duration || gridStepBeats) / gridStepBeats));
                for (let i = 1; i < durationInGridUnits; i++) {
                    const continuationKey = `${rowKey}-${gridIdx + i}`;
                    // Mark this as a continuation cell of an existing note
                    newGrid[continuationKey] = {
                        active: false, // Not the start of a note
                        continuation: true,
                        noteId: note.id,
                        originalNote: note,
                        startKey: key // Reference to the start cell
                    };
                }
            }
        });
        setGrid(newGrid);
    }, [clipNotes, rows, isDrums, gridStepBeats]);

    // Handle drag selection - mouse down to start selection rectangle
    const handleSelectionMouseDown = (e) => {
        // Only allow drag selection in select mode and not clicking on a note
        if (cursorMode !== 'select') return;

        // Check if clicking on empty space (not on a note)
        const target = e.target;
        const isEmptyCell = target.style.background === 'transparent' ||
            !target.style.background ||
            target.style.background === '';

        if (!isEmptyCell) return;

        const gridRect = gridRef.current.getBoundingClientRect();
        const startX = e.clientX - gridRect.left + gridRef.current.scrollLeft;
        const startY = e.clientY - gridRect.top + gridRef.current.scrollTop;

        dragStartRef.current = { x: startX, y: startY };
        setIsDraggingSelection(true);
        setSelectionRect({ x: startX, y: startY, width: 0, height: 0 });

        e.preventDefault();
        e.stopPropagation();
    };

    // Handle mouse move during drag selection
    const handleSelectionMouseMove = (e) => {
        if (!isDraggingSelection || !dragStartRef.current) return;

        const gridRect = gridRef.current.getBoundingClientRect();
        const currentX = e.clientX - gridRect.left + gridRef.current.scrollLeft;
        const currentY = e.clientY - gridRect.top + gridRef.current.scrollTop;

        const x = Math.min(dragStartRef.current.x, currentX);
        const y = Math.min(dragStartRef.current.y, currentY);
        const width = Math.abs(currentX - dragStartRef.current.x);
        const height = Math.abs(currentY - dragStartRef.current.y);

        setSelectionRect({ x, y, width, height });

        e.preventDefault();
    };

    // Handle mouse up to complete selection
    const handleSelectionMouseUp = (e) => {
        if (!isDraggingSelection) return;

        // Find all notes within the selection rectangle
        if (selectionRect && selectionRect.width > 5 && selectionRect.height > 5) {
            const selectedNoteIds = [];

            // Check each note's position against the selection rectangle
            Object.entries(grid).forEach(([key, cellData]) => {
                if (cellData.active && cellData.noteId) {
                    const { rowKey } = parseGridCellKey(key);

                    // Find row index
                    const rowIndex = rows.findIndex(r =>
                        (isDrums ? `${r.midi}` : r.name) === rowKey
                    );

                    if (rowIndex !== -1) {
                        const note = cellData.originalNote;
                        const startBeat = note?.start ?? 0;
                        const dur = note?.duration ?? cellData.duration ?? 0.25;
                        const noteX = startBeat * pixelsPerBeat;
                        const noteY = rowIndex * 24; // 24px per row
                        const noteWidth = Math.max(cellWidth, dur * pixelsPerBeat);
                        const noteHeight = 24;

                        // Check if note intersects with selection rectangle
                        const intersects = !(
                            noteX + noteWidth < selectionRect.x ||
                            noteX > selectionRect.x + selectionRect.width ||
                            noteY + noteHeight < selectionRect.y ||
                            noteY > selectionRect.y + selectionRect.height
                        );

                        if (intersects && !selectedNoteIds.includes(cellData.noteId)) {
                            selectedNoteIds.push(cellData.noteId);
                        }
                    }
                }
            });

            // Notify parent of selected notes
            if (onNoteClick && selectedNoteIds.length > 0) {
                onNoteClick({
                    preventDefault: () => { },
                    stopPropagation: () => { },
                    type: 'multi-select',
                    selectedIds: selectedNoteIds
                }, null);
            }
        } else if (onNoteClick && selectedNotes.length > 0) {
            // Click on empty space should clear selection (but not when clicking notes)
            onNoteClick({
                preventDefault: () => { },
                stopPropagation: () => { },
                type: 'clear-selection'
            }, null);
        }

        // Reset drag state
        setIsDraggingSelection(false);
        setSelectionRect(null);
        dragStartRef.current = null;

        e.preventDefault();
    };

    // Add global mouse event listeners for drag selection
    useEffect(() => {
        if (isDraggingSelection) {
            document.addEventListener('mousemove', handleSelectionMouseMove);
            document.addEventListener('mouseup', handleSelectionMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleSelectionMouseMove);
                document.removeEventListener('mouseup', handleSelectionMouseUp);
            };
        }
    }, [isDraggingSelection, selectionRect, grid, rows, isDrums, onNoteClick, selectedNotes]);

    const clamp = React.useCallback((value, min, max) => Math.min(Math.max(value, min), max), []);

    const handleNoteDragMouseMove = React.useCallback((moveEvent) => {
        const drag = noteDragRef.current;
        if (!drag) return;

        moveEvent.preventDefault();

        const dx = moveEvent.clientX - drag.startClientX;
        const dy = moveEvent.clientY - drag.startClientY;

        const proposedTimeDelta = Math.round(dx / cellWidth);
        const proposedRowDelta = Math.round(dy / 24); // 24px per row

        const clampedTimeDelta = clamp(proposedTimeDelta, drag.minTimeDelta, drag.maxTimeDelta);
        const clampedRowDelta = clamp(proposedRowDelta, drag.minRowDelta, drag.maxRowDelta);

        if (clampedTimeDelta === drag.lastTimeDelta && clampedRowDelta === drag.lastRowDelta) {
            return;
        }

        const activeRows = rowsRef.current || [];
        const updatedNotes = drag.notes.map(({ note, startGridPos, rowIndex }) => {
            const nextRowIndex = rowIndex + clampedRowDelta;
            const nextGridPos = startGridPos + clampedTimeDelta;
            const nextPitch = activeRows[nextRowIndex]?.midi ?? note.pitch;
            const nextStart = gridIndexToBeat(nextGridPos, gridStepBeats);

            return {
                ...note,
                pitch: nextPitch,
                start: nextStart
            };
        });

        if (onNoteClickRef.current) {
            onNoteClickRef.current({
                preventDefault: () => { },
                stopPropagation: () => { },
                type: 'move',
                notes: updatedNotes
            }, null);
        }

        drag.lastTimeDelta = clampedTimeDelta;
        drag.lastRowDelta = clampedRowDelta;
    }, [clamp, cellWidth, gridStepBeats]);

    const handleNoteDragMouseUp = React.useCallback(() => {
        setIsDraggingNotes(false);
        noteDragRef.current = null;
    }, []);

    // Add global mouse event listeners for note dragging
    useEffect(() => {
        if (!isDraggingNotes) return;

        document.addEventListener('mousemove', handleNoteDragMouseMove);
        document.addEventListener('mouseup', handleNoteDragMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleNoteDragMouseMove);
            document.removeEventListener('mouseup', handleNoteDragMouseUp);
        };
    }, [isDraggingNotes, handleNoteDragMouseMove, handleNoteDragMouseUp]);

    const startNoteDrag = (e, noteId, originalNote) => {
        if (cursorMode !== 'select') return;
        if (!noteId || !originalNote) return;
        if (e.button !== 0) return;

        e.preventDefault();
        e.stopPropagation();

        // Ctrl/cmd-click toggles selection without starting a drag
        if (e.ctrlKey || e.metaKey) {
            if (onNoteClickRef.current) onNoteClickRef.current(e, originalNote);
            return;
        }

        const isAlreadySelected = selectedNotes.includes(noteId);
        if (!isAlreadySelected && onNoteClickRef.current) {
            onNoteClickRef.current(e, originalNote);
        }

        const noteIdsToMove = isAlreadySelected ? selectedNotes : [noteId];
        const notesToMove = noteIdsToMove
            .map(id => clipNotes.find(n => n.id === id))
            .filter(Boolean);

        if (notesToMove.length === 0) return;

        const activeRows = rowsRef.current || [];
        const noteStartData = notesToMove
            .map(note => {
                const rowIndex = activeRows.findIndex(r => r.midi === note.pitch);
                if (rowIndex === -1) return null;

                const startGridPos = beatToGridIndex(note.start || 0, gridStepBeats);
                const durationInGridUnits = Math.max(1, Math.round((note.duration || gridStepBeats) / gridStepBeats));

                return {
                    note,
                    rowIndex,
                    startGridPos,
                    durationInGridUnits
                };
            })
            .filter(Boolean);

        if (noteStartData.length === 0) return;

        const minRowIndex = Math.min(...noteStartData.map(n => n.rowIndex));
        const maxRowIndex = Math.max(...noteStartData.map(n => n.rowIndex));
        const minStartGridPos = Math.min(...noteStartData.map(n => n.startGridPos));
        const maxEndGridPos = Math.max(...noteStartData.map(n => n.startGridPos + n.durationInGridUnits - 1));

        noteDragRef.current = {
            startClientX: e.clientX,
            startClientY: e.clientY,
            notes: noteStartData,
            lastTimeDelta: 0,
            lastRowDelta: 0,
            minRowDelta: -minRowIndex,
            maxRowDelta: (activeRows.length - 1) - maxRowIndex,
            minTimeDelta: -minStartGridPos,
            maxTimeDelta: (totalSteps - 1) - maxEndGridPos
        };

        setIsDraggingNotes(true);
    };

    const toggleNote = (rowKey, gridIndex) => {
        const key = `${rowKey}-${gridIndex}`;
        const noteObj = isDrums
            ? rows.find(n => String(n.midi) === String(rowKey))
            : rows.find(n => n.name === rowKey);

        if (!noteObj || !selectedClip) return;

        const gridCell = grid[key];
        const isActive = gridCell && (gridCell.active || gridCell.continuation);

        if (isActive) {
            // Remove note - find and delete the corresponding note from clip
            let noteToDelete = gridCell?.originalNote;

            if (!noteToDelete && gridCell?.noteId) {
                noteToDelete = clipNotes.find(n => n.id === gridCell.noteId);
            }

            if (!noteToDelete) {
                // Otherwise find the note directly (fallback for non-drum notes)
                noteToDelete = clipNotes.find(note => {
                    const noteNameFromMidi = allNotes.find(n => n.midi === note.pitch)?.name;
                    const noteGridPos = beatToGridIndex(note.start, gridStepBeats);
                    return noteNameFromMidi === rowKey && noteGridPos === gridIndex;
                });
            }

            if (noteToDelete && onNoteContextMenu) {
                onNoteContextMenu({ preventDefault: () => { } }, noteToDelete);
            }
        } else {
            const startTime = gridIndexToBeat(gridIndex, gridStepBeats);
            if (onGridClick) {
                onGridClick(noteObj.midi, startTime);
            }
        }

        // Note: We don't need to update the grid state here as it will be updated
        // when the clipNotes prop changes, which triggers the useEffect
    };

    // Preview sounds
    const ensureCtx = () => {
        if (!audioCtxRef.current) {
            audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended') ctx.resume();
        return ctx;
    };

    const ensureSoundfontInstrument = async (instrumentName) => {
        // Return existing instrument if already loaded
        if (soundfontInstrumentsRef.current[instrumentName]) {
            return soundfontInstrumentsRef.current[instrumentName];
        }

        // Prevent multiple simultaneous loads
        if (soundfontLoadingRef.current[instrumentName]) {
            return soundfontLoadingRef.current[instrumentName];
        }

        const ctx = ensureCtx();

        console.log(`🎵 Loading ${instrumentName}...`);

        // Start loading - use Soundfont.instrument() as per documentation
        soundfontLoadingRef.current[instrumentName] = Soundfont.instrument(ctx, instrumentName, {
            soundfont: 'MusyngKite',
            format: 'mp3'
        }).then(instrument => {
            soundfontInstrumentsRef.current[instrumentName] = instrument;
            soundfontLoadingRef.current[instrumentName] = null;
            console.log(`✅ ${instrumentName} loaded successfully`);
            return instrument;
        }).catch(err => {
            console.error(`❌ ${instrumentName} failed to load:`, err);
            soundfontLoadingRef.current[instrumentName] = null;
            throw err;
        });

        return soundfontLoadingRef.current[instrumentName];
    };

    // Convert note name (e.g., "C4") to MIDI number
    const noteNameToMidi = (noteName) => {
        const noteOrder = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const match = noteName.match(/^([A-G]#?)(\d)$/);
        if (!match) return 60; // Default to C4
        const [, pitch, octaveStr] = match;
        const octave = parseInt(octaveStr);
        const pitchIndex = noteOrder.indexOf(pitch);
        return (octave + 1) * 12 + pitchIndex;
    };

    const playTestNote = (noteName, duration = 0.5) => {
        ensureSoundfontInstrument(previewInstrument).then((instrument) => {
            const ctx = audioCtxRef.current;

            // Play the note using soundfont-player
            // Use note name directly (e.g., "C4") as per soundfont-player documentation
            instrument.play(noteName, ctx.currentTime, {
                duration: duration,
                gain: 0.8
            });
        }).catch(() => {
            // Fallback to simple sine if the soundfont fails to load
            const ctx = ensureCtx();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = noteToFrequency(noteName);
            gain.gain.setValueAtTime(0, ctx.currentTime);
            gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.01);
            gain.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + duration);
        });
    };

    const playDrumPreview = (midi) => {
        const type = instrumentConfigs?.drums?.drumMap?.[midi]?.name || '';
        // Use the main audio engine if available to preview the actual sample
        const engine = window.webAudioEngine;
        if (engine && typeof engine.playDrumSampleByName === 'function') {
            try {
                engine.playDrumSampleByName(type, currentTrack?.id, 0.9);
                return;
            } catch (e) {
                // Fall through to synthetic preview on error
                console.warn('Drum sample preview failed, using synthetic sound:', e);
            }
        }

        // Fallback synthetic preview (in case engine is not ready)
        const ctx = ensureCtx();
        const now = ctx.currentTime;

        const whiteNoise = () => {
            const bufferSize = ctx.sampleRate * 1.0;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
            const node = ctx.createBufferSource();
            node.buffer = buffer;
            node.loop = true;
            return node;
        };

        if (type === 'kick') {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(150, now);
            osc.frequency.exponentialRampToValueAtTime(50, now + 0.12);
            gain.gain.setValueAtTime(1, now);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
            osc.connect(gain).connect(ctx.destination);
            osc.start(now);
            osc.stop(now + 0.2);
        } else if (type === 'snare') {
            const noise = whiteNoise();
            const bp = ctx.createBiquadFilter();
            bp.type = 'bandpass';
            bp.frequency.value = 2000;
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.7, now);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
            noise.connect(bp).connect(gain).connect(ctx.destination);
            noise.start(now);
            noise.stop(now + 0.2);
        } else {
            // Hi-hats, cymbals
            const noise = whiteNoise();
            const hp = ctx.createBiquadFilter();
            hp.type = 'highpass';
            hp.frequency.value = 6000;
            const gain = ctx.createGain();
            const dur = (type === 'openhat') ? 0.5 : 0.12;
            gain.gain.setValueAtTime(0.5, now);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
            noise.connect(hp).connect(gain).connect(ctx.destination);
            noise.start(now);
            noise.stop(now + dur);
        }
    };

    // Timeline header ref for horizontal scrolling sync
    const timelineHeaderRef = useRef(null);

    const ensureViewBarsForPx = React.useCallback((targetRightPx) => {
        const requiredBeats = Math.ceil(targetRightPx / pixelsPerBeat);
        const requiredBars = Math.ceil(requiredBeats / beatsPerBar);
        const paddedBars = Math.max(minBars, requiredBars + 4);
        setViewBars(prev => Math.max(prev, paddedBars));
    }, [beatsPerBar, minBars, pixelsPerBeat]);

    useEffect(() => {
        // Ensure the grid covers the visible viewport even before the first scroll.
        const gridEl = gridRef.current;
        if (!gridEl) return;
        ensureViewBarsForPx(gridEl.scrollLeft + gridEl.clientWidth * 2);
    }, [ensureViewBarsForPx]);

    // Scroll synchronization handlers
    const handleGridScroll = () => {
        const gridEl = gridRef.current;
        if (!gridEl) return;

        if (pianoKeysRef.current) {
            pianoKeysRef.current.scrollTop = gridEl.scrollTop;
        }
        if (timelineHeaderRef.current) {
            timelineHeaderRef.current.scrollLeft = gridEl.scrollLeft;
        }
        // Sync velocity lane horizontal scroll
        if (velocityLaneRef.current) {
            velocityLaneRef.current.scrollLeft = gridEl.scrollLeft;
        }

        ensureViewBarsForPx(gridEl.scrollLeft + gridEl.clientWidth * 2);
    };

    const handleKeysScroll = () => {
        if (gridRef.current && pianoKeysRef.current) {
            gridRef.current.scrollTop = pianoKeysRef.current.scrollTop;
        }
    };

    // Ref for the main container
    const containerRef = useRef(null);

    // Add wheel event listener with passive: false to enable preventDefault
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            // Prevent the page scroll
            e.preventDefault();
            e.stopPropagation();

            if (gridRef.current) {
                // Apply scroll to grid
                gridRef.current.scrollTop += e.deltaY;
                gridRef.current.scrollLeft += e.deltaX;

                // Sync piano keys vertical scroll
                if (pianoKeysRef.current) {
                    pianoKeysRef.current.scrollTop = gridRef.current.scrollTop;
                }

                // Sync timeline horizontal scroll
                if (timelineHeaderRef.current) {
                    timelineHeaderRef.current.scrollLeft = gridRef.current.scrollLeft;
                }

                // Sync velocity lane horizontal scroll
                if (velocityLaneRef.current) {
                    velocityLaneRef.current.scrollLeft = gridRef.current.scrollLeft;
                }

                ensureViewBarsForPx(gridRef.current.scrollLeft + gridRef.current.clientWidth * 2);
            }
        };

        // Add event listener with passive: false to allow preventDefault
        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [ensureViewBarsForPx]);

    // Handle velocity bar drag
    const handleVelocityDrag = (noteId, e) => {
        e.preventDefault();
        e.stopPropagation();

        const startY = e.clientY;
        const note = clipNotes.find(n => n.id === noteId);
        if (!note) return;

        // Check if this note is part of a multi-selection
        const isMultiSelect = selectedNotes.length > 1 && selectedNotes.includes(noteId);
        const notesToUpdate = isMultiSelect ? selectedNotes : [noteId];

        // Store original velocities for all notes to update
        const originalVelocities = {};
        notesToUpdate.forEach(id => {
            const n = clipNotes.find(cn => cn.id === id);
            if (n) {
                originalVelocities[id] = n.velocity ?? 0.8;
            }
        });

        const handleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            const dy = startY - moveEvent.clientY; // Inverted: drag up = increase
            const velocityChange = dy / (VELOCITY_LANE_HEIGHT - 10); // Scale to lane height

            // Update all selected notes
            notesToUpdate.forEach(id => {
                const originalVelocity = originalVelocities[id];
                if (originalVelocity !== undefined) {
                    const newVelocity = Math.max(0.01, Math.min(1, originalVelocity + velocityChange));
                    if (onNoteUpdate) {
                        onNoteUpdate(id, { velocity: newVelocity });
                    }
                }
            });
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Sync velocity lane scroll with grid
    const handleVelocityLaneScroll = () => {
        if (velocityLaneRef.current && gridRef.current) {
            velocityLaneRef.current.scrollLeft = gridRef.current.scrollLeft;
        }
    };

    // Apply quantization to selected notes
    const applyQuantize = useCallback(() => {
        if (selectedNotes.length === 0 || !onNoteClick) return;

        const notesToQuantize = clipNotes.filter(n => selectedNotes.includes(n.id));
        if (notesToQuantize.length === 0) return;

        // Store original positions if not already stored
        notesToQuantize.forEach(note => {
            if (!originalNotePositionsRef.current.has(note.id)) {
                originalNotePositionsRef.current.set(note.id, {
                    start: note.start,
                    pitch: note.pitch
                });
            }
        });

        const tripletSpec = getTripletQuantizeSpec(quantizeValue);

        let quantizedNotes;

        if (tripletSpec && notesToQuantize.length >= 2) {
            // Group triplet quantize: e.g. quarter triplet = 3 equal steps in 2 beats (0, 2/3, 4/3),
            // not independent snap to every multiple of 2/3 (which wrongly pulls the 3rd note to beat 2).
            const withOrig = notesToQuantize.map(note => ({
                note,
                original: originalNotePositionsRef.current.get(note.id) || {
                    start: note.start,
                    pitch: note.pitch
                }
            }));
            withOrig.sort((a, b) => a.original.start - b.original.start);
            const sortedStarts = withOrig.map(x => x.original.start);
            const targets = assignTripletGroupTargets(sortedStarts, tripletSpec.periodBeats);
            const strengthFactor = quantizeStrength / 100;

            quantizedNotes = withOrig.map(({ note, original }, idx) => {
                const targetStart = targets[idx];
                const quantizedStart =
                    original.start + (targetStart - original.start) * strengthFactor;

                let quantizedPitch = note.pitch;
                if (scaleQuantize && scaleQuantize.length > 0) {
                    quantizedPitch = quantizeNoteToScale(note.pitch, scaleQuantize, scaleRoot);
                }

                return {
                    ...note,
                    start: Math.max(0, quantizedStart),
                    pitch: quantizedPitch
                };
            });
        } else {
            quantizedNotes = notesToQuantize.map(note => {
                const original = originalNotePositionsRef.current.get(note.id) || {
                    start: note.start,
                    pitch: note.pitch
                };

                const quantizedStart = quantizeNoteTime(
                    original.start,
                    quantizeValue,
                    quantizeStrength,
                    quantizeSwing
                );

                let quantizedPitch = note.pitch;
                if (scaleQuantize && scaleQuantize.length > 0) {
                    quantizedPitch = quantizeNoteToScale(note.pitch, scaleQuantize, scaleRoot);
                }

                return {
                    ...note,
                    start: quantizedStart,
                    pitch: quantizedPitch
                };
            });
        }

        // Update notes via the move handler
        onNoteClick({
            preventDefault: () => { },
            stopPropagation: () => { },
            type: 'move',
            notes: quantizedNotes
        }, null);
    }, [selectedNotes, clipNotes, onNoteClick, quantizeValue, quantizeStrength, quantizeSwing, scaleQuantize, scaleRoot]);

    // Reset quantization (restore original positions)
    const resetQuantize = useCallback(() => {
        if (selectedNotes.length === 0 || !onNoteClick) return;

        const notesToReset = clipNotes.filter(n =>
            selectedNotes.includes(n.id) && originalNotePositionsRef.current.has(n.id)
        );

        if (notesToReset.length === 0) return;

        const resetNotes = notesToReset.map(note => {
            const original = originalNotePositionsRef.current.get(note.id);
            return {
                ...note,
                start: original.start,
                pitch: original.pitch
            };
        });

        // Clear stored positions for reset notes
        notesToReset.forEach(note => {
            originalNotePositionsRef.current.delete(note.id);
        });

        onNoteClick({
            preventDefault: () => { },
            stopPropagation: () => { },
            type: 'move',
            notes: resetNotes
        }, null);
    }, [selectedNotes, clipNotes, onNoteClick]);

    // Transpose notes by semitones
    // If notes are selected, transpose only selected notes
    // If no notes are selected, transpose ALL notes in the clip
    const MIN_PITCH = 24;  // C1
    const MAX_PITCH = 108; // C8

    const transposeNotes = useCallback((semitones) => {
        if (!onNoteClick || clipNotes.length === 0) return;

        // Determine which notes to transpose
        const notesToTranspose = selectedNotes.length > 0
            ? clipNotes.filter(n => selectedNotes.includes(n.id))
            : clipNotes; // Transpose ALL notes if none selected

        if (notesToTranspose.length === 0) return;

        // Check if transpose would push any note out of bounds
        const wouldExceedBounds = notesToTranspose.some(note => {
            const newPitch = note.pitch + semitones;
            return newPitch < MIN_PITCH || newPitch > MAX_PITCH;
        });

        if (wouldExceedBounds) {
            console.warn(`⚠️ Transpose would exceed pitch bounds (${MIN_PITCH}-${MAX_PITCH})`);
            return;
        }

        // Transpose each note
        const transposedNotes = notesToTranspose.map(note => ({
            ...note,
            pitch: note.pitch + semitones
        }));

        // Update notes via the move handler
        onNoteClick({
            preventDefault: () => { },
            stopPropagation: () => { },
            type: 'move',
            notes: transposedNotes
        }, null);
    }, [selectedNotes, clipNotes, onNoteClick]);

    // Keyboard shortcuts for transpose (Arrow keys with modifiers)
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Don't handle if typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

            // Arrow Up/Down for transpose
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                const direction = e.key === 'ArrowUp' ? 1 : -1;

                if (e.shiftKey) {
                    // Shift + Arrow = octave (12 semitones)
                    e.preventDefault();
                    transposeNotes(direction * 12);
                } else if (e.altKey || e.metaKey) {
                    // Alt/Cmd + Arrow = semitone
                    e.preventDefault();
                    transposeNotes(direction);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [transposeNotes]);

    // Get selected note's velocity for the control panel
    const displayVelocity = React.useMemo(() => {
        if (selectedNotes.length === 0) {
            return 64; // Default to 64 (50% of 127)
        }

        // Calculate average velocity of all selected notes
        const selectedNotesData = clipNotes.filter(n => selectedNotes.includes(n.id));
        if (selectedNotesData.length === 0) {
            return 64;
        }

        const avgVelocity = selectedNotesData.reduce((sum, note) => {
            return sum + (note.velocity ?? 0.5);
        }, 0) / selectedNotesData.length;

        return Math.round(avgVelocity * 127);
    }, [selectedNotes, clipNotes]);

    // Handle panel resize
    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        setIsResizingPanel(true);
        resizeStartRef.current = {
            x: e.clientX,
            width: leftPanelWidth
        };
    }, [leftPanelWidth]);

    const handleResizeMove = useCallback((e) => {
        if (!isResizingPanel) return;

        const deltaX = e.clientX - resizeStartRef.current.x;
        const newWidth = Math.max(120, Math.min(400, resizeStartRef.current.width + deltaX));
        setLeftPanelWidth(newWidth);
    }, [isResizingPanel]);

    const handleResizeEnd = useCallback(() => {
        setIsResizingPanel(false);
    }, []);

    // Add global listeners for panel resize
    useEffect(() => {
        if (!isResizingPanel) return;

        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);

        return () => {
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
        };
    }, [isResizingPanel, handleResizeMove, handleResizeEnd]);

    return (
        <div
            ref={containerRef}
            style={{
                display: 'flex',
                height: '100%',
                backgroundColor: '#1a1a1a',
                overflow: 'hidden',
                position: 'relative',
                boxSizing: 'border-box',
                minHeight: 0
            }}
        >
            {/* Left Control Panel */}
            <div style={{
                width: `${leftPanelWidth}px`,
                minWidth: `${leftPanelWidth}px`,
                backgroundColor: '#252525',
                borderRight: '1px solid #3a3a3a',
                display: 'flex',
                flexDirection: 'column',
                padding: '10px',
                gap: '10px',
                overflowY: 'auto',
                position: 'relative'
            }}>
                {/* Selection Info */}
                <div style={{
                    fontSize: '11px',
                    color: '#aaa',
                    borderBottom: '1px solid #3a3a3a',
                    paddingBottom: '8px'
                }}>
                    {selectedNotes.length === 0 ? (
                        <span style={{ color: '#666' }}>No note selected</span>
                    ) : selectedNotes.length === 1 ? (
                        <span style={{ color: '#4CAF50' }}>1 Note selected</span>
                    ) : (
                        <span style={{ color: '#64B5F6' }}>{selectedNotes.length} Notes selected</span>
                    )}
                </div>

                {/* Time Quantize (classic) */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #3a3a3a'
                }}>
                    <span style={{ fontSize: '10px', color: '#666', fontWeight: '500' }}>
                        Time Quantize (classic)
                    </span>

                    {/* Quantize Value + Q Button Row */}
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <select
                            value={quantizeValue}
                            onChange={(e) => setQuantizeValue(parseFloat(e.target.value))}
                            style={{
                                flex: 1,
                                height: '26px',
                                backgroundColor: '#333',
                                color: '#fff',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                outline: 'none',
                                padding: '0 4px'
                            }}
                        >
                            {QUANTIZE_DIVISIONS.map(div => (
                                <option key={div.label} value={div.value}>
                                    {div.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={applyQuantize}
                            disabled={selectedNotes.length === 0}
                            title="Apply Quantize"
                            style={{
                                width: '26px',
                                height: '26px',
                                backgroundColor: selectedNotes.length > 0 ? '#4a7c59' : '#333',
                                color: selectedNotes.length > 0 ? '#fff' : '#666',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: selectedNotes.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            Q
                        </button>
                    </div>

                    {/* Strength Slider */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ fontSize: '10px', color: '#888' }}>Strength</span>
                            <span style={{
                                fontSize: '11px',
                                color: '#fff',
                                fontFamily: 'monospace',
                                minWidth: '28px',
                                textAlign: 'right'
                            }}>
                                {quantizeStrength}
                            </span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={quantizeStrength}
                            onChange={(e) => setQuantizeStrength(parseInt(e.target.value))}
                            style={{
                                width: '100%',
                                height: '4px',
                                borderRadius: '2px',
                                background: '#444',
                                cursor: 'pointer',
                                accentColor: '#6b8e6b'
                            }}
                        />
                    </div>

                    {/* Swing Slider */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ fontSize: '10px', color: '#888' }}>Swing</span>
                            <span style={{
                                fontSize: '11px',
                                color: '#fff',
                                fontFamily: 'monospace',
                                minWidth: '28px',
                                textAlign: 'right'
                            }}>
                                {quantizeSwing}
                            </span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={quantizeSwing}
                            onChange={(e) => setQuantizeSwing(parseInt(e.target.value))}
                            style={{
                                width: '100%',
                                height: '4px',
                                borderRadius: '2px',
                                background: '#444',
                                cursor: 'pointer',
                                accentColor: '#6b8e6b'
                            }}
                        />
                    </div>
                </div>

                {/* Scale Quantize */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #3a3a3a'
                }}>
                    <span style={{ fontSize: '10px', color: '#666', fontWeight: '500' }}>
                        Scale Quantize
                    </span>

                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {/* Scale On/Off */}
                        <select
                            value={scaleQuantize ? 'on' : 'off'}
                            onChange={(e) => {
                                if (e.target.value === 'off') {
                                    setScaleQuantize(null);
                                } else {
                                    // Default to Major scale when turning on
                                    setScaleQuantize(SCALE_QUANTIZE_OPTIONS[1].value);
                                }
                            }}
                            style={{
                                width: '42px',
                                height: '26px',
                                backgroundColor: '#333',
                                color: '#fff',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                outline: 'none',
                                padding: '0 2px'
                            }}
                        >
                            <option value="off">Off</option>
                            <option value="on">On</option>
                        </select>

                        {/* Scale Type */}
                        <select
                            value={JSON.stringify(scaleQuantize)}
                            onChange={(e) => {
                                const parsed = JSON.parse(e.target.value);
                                setScaleQuantize(parsed);
                            }}
                            disabled={!scaleQuantize}
                            style={{
                                flex: 1,
                                height: '26px',
                                backgroundColor: scaleQuantize ? '#333' : '#2a2a2a',
                                color: scaleQuantize ? '#fff' : '#555',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                fontSize: '9px',
                                cursor: scaleQuantize ? 'pointer' : 'not-allowed',
                                outline: 'none',
                                padding: '0 2px'
                            }}
                        >
                            {SCALE_QUANTIZE_OPTIONS.slice(1).map(scale => (
                                <option key={scale.label} value={JSON.stringify(scale.value)}>
                                    {scale.label}
                                </option>
                            ))}
                        </select>

                        {/* Q Button for Scale */}
                        <button
                            onClick={applyQuantize}
                            disabled={selectedNotes.length === 0 || !scaleQuantize}
                            title="Apply Scale Quantize"
                            style={{
                                width: '26px',
                                height: '26px',
                                backgroundColor: (selectedNotes.length > 0 && scaleQuantize) ? '#4a7c59' : '#333',
                                color: (selectedNotes.length > 0 && scaleQuantize) ? '#fff' : '#666',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: (selectedNotes.length > 0 && scaleQuantize) ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            Q
                        </button>
                    </div>
                </div>

                {/* Transpose */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #3a3a3a'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{ fontSize: '10px', color: '#666', fontWeight: '500' }}>
                            Transpose
                        </span>
                        <span style={{
                            fontSize: '9px',
                            color: '#555',
                            fontStyle: 'italic'
                        }}>
                            {selectedNotes.length > 0
                                ? `${selectedNotes.length} note${selectedNotes.length > 1 ? 's' : ''}`
                                : 'All notes'
                            }
                        </span>
                    </div>

                    {/* Semitone controls */}
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '9px', color: '#888', width: '50px' }}>Semitone</span>
                        <button
                            onClick={() => transposeNotes(-1)}
                            disabled={clipNotes.length === 0}
                            title="Transpose down 1 semitone (Alt/Cmd + ↓)"
                            style={{
                                flex: 1,
                                height: '24px',
                                backgroundColor: clipNotes.length > 0 ? '#333' : '#2a2a2a',
                                color: clipNotes.length > 0 ? '#fff' : '#555',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: clipNotes.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            −1
                        </button>
                        <button
                            onClick={() => transposeNotes(1)}
                            disabled={clipNotes.length === 0}
                            title="Transpose up 1 semitone (Alt/Cmd + ↑)"
                            style={{
                                flex: 1,
                                height: '24px',
                                backgroundColor: clipNotes.length > 0 ? '#333' : '#2a2a2a',
                                color: clipNotes.length > 0 ? '#fff' : '#555',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: clipNotes.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            +1
                        </button>
                    </div>

                    {/* Octave controls */}
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '9px', color: '#888', width: '50px' }}>Octave</span>
                        <button
                            onClick={() => transposeNotes(-12)}
                            disabled={clipNotes.length === 0}
                            title="Transpose down 1 octave (Shift + ↓)"
                            style={{
                                flex: 1,
                                height: '24px',
                                backgroundColor: clipNotes.length > 0 ? '#333' : '#2a2a2a',
                                color: clipNotes.length > 0 ? '#fff' : '#555',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: clipNotes.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            −12
                        </button>
                        <button
                            onClick={() => transposeNotes(12)}
                            disabled={clipNotes.length === 0}
                            title="Transpose up 1 octave (Shift + ↑)"
                            style={{
                                flex: 1,
                                height: '24px',
                                backgroundColor: clipNotes.length > 0 ? '#333' : '#2a2a2a',
                                color: clipNotes.length > 0 ? '#fff' : '#555',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: clipNotes.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            +12
                        </button>
                    </div>

                    {/* Quick transpose presets - common intervals */}
                    <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                        {[
                            { val: -7, label: '−P5' },
                            { val: -5, label: '−P4' },
                            { val: -4, label: '−M3' },
                            { val: -3, label: '−m3' },
                            { val: 3, label: '+m3' },
                            { val: 4, label: '+M3' },
                            { val: 5, label: '+P4' },
                            { val: 7, label: '+P5' }
                        ].map(({ val, label }) => (
                            <button
                                key={val}
                                onClick={() => transposeNotes(val)}
                                disabled={clipNotes.length === 0}
                                title={`Transpose ${val > 0 ? '+' : ''}${val} semitones (${label})`}
                                style={{
                                    flex: 1,
                                    minWidth: '28px',
                                    height: '20px',
                                    backgroundColor: clipNotes.length > 0 ? '#2a2a2a' : '#252525',
                                    color: clipNotes.length > 0 ? '#aaa' : '#444',
                                    border: '1px solid #3a3a3a',
                                    borderRadius: '2px',
                                    fontSize: '8px',
                                    cursor: clipNotes.length > 0 ? 'pointer' : 'not-allowed',
                                    padding: 0
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Keyboard shortcut hint */}
                    <div style={{
                        fontSize: '8px',
                        color: '#555',
                        textAlign: 'center',
                        marginTop: '2px'
                    }}>
                        ⌥/⌘+↑↓ semitone • ⇧+↑↓ octave
                    </div>
                </div>

                {/* Velocity Control */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{ fontSize: '11px', color: '#888', fontWeight: '500' }}>
                            Velocity
                        </span>
                        <span style={{
                            fontSize: '14px',
                            color: '#fff',
                            fontWeight: '600',
                            fontFamily: 'monospace'
                        }}>
                            {displayVelocity}
                        </span>
                    </div>

                    {/* Velocity Slider */}
                    <input
                        type="range"
                        min="1"
                        max="127"
                        value={displayVelocity}
                        onChange={(e) => {
                            const newVelocity = parseInt(e.target.value) / 127;
                            // Update all selected notes
                            selectedNotes.forEach(noteId => {
                                if (onNoteUpdate) {
                                    onNoteUpdate(noteId, { velocity: newVelocity });
                                }
                            });
                        }}
                        disabled={selectedNotes.length === 0}
                        style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: selectedNotes.length > 0
                                ? `linear-gradient(to right, 
                                    hsl(280, 70%, 45%) 0%, 
                                    hsl(120, 70%, 45%) 50%, 
                                    hsl(0, 70%, 45%) 100%)`
                                : '#333',
                            cursor: selectedNotes.length > 0 ? 'pointer' : 'not-allowed',
                            opacity: selectedNotes.length > 0 ? 1 : 0.5
                        }}
                    />

                    {/* Velocity Color Preview */}
                    <div style={{
                        height: '8px',
                        borderRadius: '4px',
                        backgroundColor: selectedNotes.length > 0
                            ? getVelocityColor(displayVelocity / 127)
                            : '#333',
                        transition: 'background-color 0.15s ease'
                    }} />
                </div>

                {/* Quick Velocity Presets */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>
                        Presets
                    </span>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {[32, 64, 96, 127].map(vel => (
                            <button
                                key={vel}
                                onClick={() => {
                                    const newVelocity = vel / 127;
                                    selectedNotes.forEach(noteId => {
                                        if (onNoteUpdate) {
                                            onNoteUpdate(noteId, { velocity: newVelocity });
                                        }
                                    });
                                }}
                                disabled={selectedNotes.length === 0}
                                style={{
                                    flex: 1,
                                    padding: '4px 2px',
                                    fontSize: '10px',
                                    backgroundColor: selectedNotes.length > 0 ? '#333' : '#2a2a2a',
                                    color: selectedNotes.length > 0 ? '#fff' : '#555',
                                    border: '1px solid #444',
                                    borderRadius: '3px',
                                    cursor: selectedNotes.length > 0 ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {vel}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Velocity Legend */}
                {/* <div style={{
                    marginTop: 'auto',
                    paddingTop: '10px',
                    borderTop: '1px solid #3a3a3a'
                }}>
                    <span style={{ fontSize: '9px', color: '#555', display: 'block', marginBottom: '4px' }}>
                        Velocity Colors
                    </span>
                    <div style={{
                        display: 'flex',
                        height: '12px',
                        borderRadius: '3px',
                        overflow: 'hidden'
                    }}>
                        <div style={{ flex: 1, background: 'hsl(280, 70%, 45%)' }} title="Low (0-42)" />
                        <div style={{ flex: 1, background: 'hsl(200, 70%, 45%)' }} title="Low-Mid (43-63)" />
                        <div style={{ flex: 1, background: 'hsl(120, 70%, 45%)' }} title="Mid (64)" />
                        <div style={{ flex: 1, background: 'hsl(60, 70%, 45%)' }} title="Mid-High (65-95)" />
                        <div style={{ flex: 1, background: 'hsl(0, 70%, 45%)' }} title="High (96-127)" />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '8px',
                        color: '#555',
                        marginTop: '2px'
                    }}>
                        <span>Soft</span>
                        <span>Hard</span>
                    </div>
                </div> */}

                {/* Resize Handle */}
                <div
                    onMouseDown={handleResizeStart}
                    className="resize-handle"
                    style={{
                        position: 'absolute',
                        right: '-3px',
                        top: 0,
                        bottom: 0,
                        width: '6px',
                        cursor: 'col-resize',
                        backgroundColor: isResizingPanel ? '#4a7c59' : 'transparent',
                        transition: 'background-color 0.15s',
                        zIndex: 100,
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                        if (!isResizingPanel) {
                            e.currentTarget.style.backgroundColor = '#4a4a4a';
                            const grip = e.currentTarget.querySelector('.grip-indicator');
                            if (grip) grip.style.opacity = '0.8';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isResizingPanel) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            const grip = e.currentTarget.querySelector('.grip-indicator');
                            if (grip) grip.style.opacity = '0';
                        }
                    }}
                    title="Drag to resize panel"
                >
                    {/* Visual grip indicator */}
                    <div
                        className="grip-indicator"
                        style={{
                            width: '2px',
                            height: '40px',
                            backgroundColor: isResizingPanel ? '#6b8e6b' : '#888',
                            borderRadius: '1px',
                            opacity: isResizingPanel ? 1 : 0,
                            transition: 'opacity 0.2s',
                            pointerEvents: 'none'
                        }}
                    />
                </div>
            </div>

            {/* Main Piano Roll Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                minHeight: 0
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 0
                }}>
                    {/* Instrument Selector */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '80px',
                        height: '40px',
                        backgroundColor: '#3a3a3a',
                        borderRight: '1px solid #4a4a4a',
                        borderBottom: '1px solid #4a4a4a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 11,
                        padding: '4px'
                    }}>
                        <select
                            value={previewInstrument}
                            onChange={(e) => {
                                const newInstrument = e.target.value;
                                setPreviewInstrument(newInstrument);
                                // Notify parent to store instrument with track
                                if (onInstrumentChange && currentTrack) {
                                    onInstrumentChange(currentTrack.id, newInstrument);
                                }
                            }}
                            style={{
                                width: '100%',
                                height: '28px',
                                backgroundColor: '#2a2a2a',
                                color: '#fff',
                                border: '1px solid #4a4a4a',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                outline: 'none',
                                padding: '0 4px'
                            }}
                        >
                            <option value="acoustic_grand_piano">🎹 Piano</option>
                            <option value="violin">🎻 Violin</option>
                            <option value="cello">🎻 Cello</option>
                            <option value="flute">🪈 Flute</option>
                            <option value="acoustic_guitar_nylon">🎸 Guitar</option>
                            <option value="trumpet">🎺 Trumpet</option>
                            <option value="saxophone">🎷 Sax</option>
                            <option value="clarinet">🎵 Clarinet</option>
                        </select>
                    </div>

                    {/* Timeline Header */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '80px',
                        right: 0,
                        height: '40px',
                        backgroundColor: '#3a3a3a',
                        borderBottom: '1px solid #4a4a4a',
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 10,
                        overflow: 'hidden' /* Change to hidden to prevent overflow */
                    }}>
                        {/* Timeline markers - scrollable container */}
                        <div
                            ref={timelineHeaderRef}
                            style={{
                                position: 'relative',
                                height: '100%',
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                scrollbarWidth: 'none', /* Hide scrollbar */
                                msOverflowStyle: 'none'  /* Hide scrollbar in IE/Edge */
                            }}
                        >
                            {/* Timeline content: bar numbers + CSS tick lines */}
                            {(() => {
                                const barPx = beatsPerBar * pixelsPerBeat;
                                // Region boundaries in px (bar ruler always starts at beat 0 = bar 1)
                                const regionStartPx = clipStartBeats * pixelsPerBeat;
                                const regionEndPx = (clipStartBeats + clipDurationBeats) * pixelsPerBeat;

                                return (
                                    <div style={{
                                        position: 'relative',
                                        height: '100%',
                                        width: `${gridWidthPx}px`
                                    }}>
                                        {/* Top half: bar number labels — always bar 1, 2, 3… */}
                                        <div style={{ height: '20px', position: 'relative', overflow: 'hidden' }}>
                                            {/* Region highlight band */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${regionStartPx}px`,
                                                width: `${regionEndPx - regionStartPx}px`,
                                                top: 0,
                                                height: '100%',
                                                backgroundColor: 'rgba(99,179,237,0.18)',
                                                pointerEvents: 'none'
                                            }} />

                                            {Array.from({ length: viewBars }, (_, barIdx) => {
                                                const barLeftPx = barIdx * barPx;
                                                const barRightPx = barLeftPx + barPx;
                                                const inRegion = barLeftPx >= regionStartPx - 0.5 && barRightPx <= regionEndPx + 0.5;
                                                return (
                                                    <div
                                                        key={barIdx}
                                                        style={{
                                                            position: 'absolute',
                                                            left: `${barLeftPx}px`,
                                                            top: 0,
                                                            height: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            paddingLeft: '5px',
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            color: inRegion ? '#90cdf4' : '#888',
                                                            pointerEvents: 'none'
                                                        }}
                                                    >
                                                        {barIdx + 1}
                                                    </div>
                                                );
                                            })}

                                            {/* Region left border line */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${regionStartPx}px`,
                                                top: 0,
                                                width: '2px',
                                                height: '100%',
                                                backgroundColor: '#63b3ed',
                                                pointerEvents: 'none',
                                                zIndex: 2
                                            }} />
                                            {/* Region right border line */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${regionEndPx - 2}px`,
                                                top: 0,
                                                width: '2px',
                                                height: '100%',
                                                backgroundColor: '#63b3ed',
                                                pointerEvents: 'none',
                                                zIndex: 2
                                            }} />
                                        </div>

                                        {/* Bottom half: CSS-drawn tick lines — bar, beat */}
                                        <div style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: '20px',
                                            width: `${gridWidthPx}px`,
                                            height: '20px',
                                            borderTop: '1px solid #555',
                                            backgroundImage: [
                                                `repeating-linear-gradient(to right, transparent 0px, transparent ${barPx - 1}px, rgba(200,200,200,0.55) ${barPx - 1}px, rgba(200,200,200,0.55) ${barPx}px)`,
                                                `repeating-linear-gradient(to right, transparent 0px, transparent ${pixelsPerBeat - 1}px, rgba(150,150,150,0.35) ${pixelsPerBeat - 1}px, rgba(150,150,150,0.35) ${pixelsPerBeat}px)`,
                                                `repeating-linear-gradient(to right, transparent 0px, transparent ${Math.max(2, pixelsPerBeat * gridStepBeats) - 1}px, rgba(120,180,220,0.28) ${Math.max(2, pixelsPerBeat * gridStepBeats) - 1}px, rgba(120,180,220,0.28) ${Math.max(2, pixelsPerBeat * gridStepBeats)}px)`,
                                            ].join(', '),
                                            backgroundSize: `${barPx}px 100%, ${pixelsPerBeat}px 100%, ${Math.max(2, pixelsPerBeat * gridStepBeats)}px 100%`,
                                            pointerEvents: 'none'
                                        }}>
                                            {/* Region highlight in tick row */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${regionStartPx}px`,
                                                width: `${regionEndPx - regionStartPx}px`,
                                                top: 0,
                                                height: '100%',
                                                backgroundColor: 'rgba(99,179,237,0.12)',
                                                pointerEvents: 'none'
                                            }} />
                                            {/* Left border continues into tick row */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${regionStartPx}px`,
                                                top: 0,
                                                width: '2px',
                                                height: '100%',
                                                backgroundColor: '#63b3ed',
                                                pointerEvents: 'none',
                                                zIndex: 2
                                            }} />
                                            {/* Right border continues into tick row */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${regionEndPx - 2}px`,
                                                top: 0,
                                                width: '2px',
                                                height: '100%',
                                                backgroundColor: '#63b3ed',
                                                pointerEvents: 'none',
                                                zIndex: 2
                                            }} />
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Notes list (Piano Keys) */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '80px',
                        backgroundColor: '#2a2a2a',
                        borderRight: '1px solid #3a3a3a',
                        position: 'relative',
                        zIndex: 5,
                        height: '100%'
                    }}>
                        {/* Header spacer */}
                        <div style={{
                            height: '40px',
                            backgroundColor: '#3a3a3a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            color: '#999',
                            fontWeight: '500',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            zIndex: 6
                        }}>
                            {isDrums ? 'Drums' : 'Keys'}
                        </div>

                        {/* Piano keys or Drum list */}
                        <div
                            ref={pianoKeysRef}
                            style={{
                                overflowY: 'scroll',
                                flex: 1,
                                scrollbarWidth: 'thin',
                                msOverflowStyle: 'auto',
                                marginTop: '40px', /* Add margin to prevent overlap with header */
                                minHeight: 0,
                                maxHeight: 'calc(100% - 40px)'
                            }}
                            className="piano-keys-container"
                            onScroll={handleKeysScroll}
                        >
                            <div style={{
                                minHeight: `${rows.length * 24}px` /* Force content height based on number of rows */
                            }}>
                                {rows.map((row) => {
                                    if (isDrums) {
                                        return (
                                            <div
                                                key={`drum-${row.midi}`}
                                                style={{
                                                    height: '24px',
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    background: '#f8f8f8',
                                                    color: '#222',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.1s ease',
                                                    borderBottom: '1px solid #555',
                                                    boxSizing: 'border-box'
                                                }}
                                                onClick={() => playDrumPreview(row.midi)}
                                                onMouseEnter={(e) => { e.target.style.backgroundColor = '#e8e8e8'; }}
                                                onMouseLeave={(e) => { e.target.style.backgroundColor = '#f8f8f8'; }}
                                            >
                                                {row.label}
                                            </div>
                                        );
                                    }
                                    const isC = row.name.startsWith('C');
                                    return (
                                        <div
                                            key={row.name}
                                            style={{
                                                height: '24px',
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                fontWeight: isC ? '600' : '400',
                                                background: row.isBlack ? '#333' : (isC ? '#f8f8f8' : '#fff'),
                                                color: row.isBlack ? '#fff' : (isC ? '#000' : '#444'),
                                                cursor: 'pointer',
                                                transition: 'all 0.1s ease',
                                                borderBottom: isC ? '2px solid #ddd' : '1px solid #555',
                                                borderTop: row.isBlack ? 'none' : '1px solid #666',
                                                boxSizing: 'border-box'
                                            }}
                                            onClick={() => playTestNote(row.name)}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = row.isBlack ? '#444' : '#e8e8e8';
                                                e.target.style.transform = 'scale(1.02)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = row.isBlack ? '#333' : (isC ? '#f8f8f8' : '#fff');
                                                e.target.style.transform = 'scale(1)';
                                            }}
                                        >
                                            {row.name}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Piano roll grid */}
                    <div
                        ref={gridRef}
                        onScroll={handleGridScroll}
                        style={{
                            flex: 1,
                            overflowX: 'scroll',
                            overflowY: 'scroll',
                            backgroundColor: '#1a1a1a',
                            position: 'relative',
                            marginTop: '40px', /* Add margin to prevent overlap with header */
                            minHeight: 0,
                            maxHeight: 'calc(100% - 40px)',
                            cursor: isDraggingSelection ? 'crosshair' : 'default'
                        }}
                    >
                        {/* Header spacer - not needed with margin */}

                        {/* Grid content container with fixed width to match timeline */}
                        <div style={{
                            width: `${gridWidthPx}px`,
                            position: 'relative',
                            minHeight: `${rows.length * 24}px` /* Force content height based on number of rows */
                        }}>
                            {/* Region highlight band in the note grid */}
                            {clipDurationBeats > 0 && (() => {
                                const regionStartPx = clipStartBeats * pixelsPerBeat;
                                const regionEndPx = (clipStartBeats + clipDurationBeats) * pixelsPerBeat;
                                return (
                                    <>
                                        {/* Dimmed overlay outside region — left side */}
                                        {regionStartPx > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                width: `${regionStartPx}px`,
                                                height: '100%',
                                                backgroundColor: 'rgba(0,0,0,0.35)',
                                                pointerEvents: 'none',
                                                zIndex: 1
                                            }} />
                                        )}
                                        {/* Dimmed overlay outside region — right side */}
                                        <div style={{
                                            position: 'absolute',
                                            left: `${regionEndPx}px`,
                                            top: 0,
                                            width: `${gridWidthPx - regionEndPx}px`,
                                            height: '100%',
                                            backgroundColor: 'rgba(0,0,0,0.35)',
                                            pointerEvents: 'none',
                                            zIndex: 1
                                        }} />
                                        {/* Left region border */}
                                        <div style={{
                                            position: 'absolute',
                                            left: `${regionStartPx}px`,
                                            top: 0,
                                            width: '2px',
                                            height: '100%',
                                            backgroundColor: '#63b3ed',
                                            pointerEvents: 'none',
                                            zIndex: 4
                                        }} />
                                        {/* Right region border */}
                                        <div style={{
                                            position: 'absolute',
                                            left: `${regionEndPx - 2}px`,
                                            top: 0,
                                            width: '2px',
                                            height: '100%',
                                            backgroundColor: '#63b3ed',
                                            pointerEvents: 'none',
                                            zIndex: 4
                                        }} />
                                    </>
                                );
                            })()}

                            {/* Selection rectangle overlay */}
                            {selectionRect && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: selectionRect.x,
                                        top: selectionRect.y,
                                        width: selectionRect.width,
                                        height: selectionRect.height,
                                        border: '2px dashed #64B5F6',
                                        backgroundColor: 'rgba(100, 181, 246, 0.2)',
                                        pointerEvents: 'none',
                                        zIndex: 1000
                                    }}
                                />
                            )}

                            {/* Note rectangles - rendered as continuous bars */}
                            {clipNotes.map(note => {
                                const noteObj = rows.find(n => n.midi === note.pitch);
                                if (!noteObj) return null;

                                const rowIndex = rows.findIndex(n => n.midi === note.pitch);
                                const noteLeft = note.start * pixelsPerBeat;
                                const noteWidth = Math.max(cellWidth, note.duration * pixelsPerBeat);
                                const noteTop = rowIndex * 24;
                                const isSelected = selectedNotes.includes(note.id);
                                const velocity = note.velocity ?? 0.8; // Default velocity
                                const noteColor = getVelocityColor(velocity, isSelected);

                                return (
                                    <div
                                        key={`note-rect-${note.id}`}
                                        style={{
                                            position: 'absolute',
                                            left: `${noteLeft}px`,
                                            top: `${noteTop + 2}px`,
                                            width: `${noteWidth}px`,
                                            height: '20px',
                                            backgroundColor: noteColor,
                                            borderRadius: '3px',
                                            border: `1px solid ${isSelected ? '#90CAF9' : 'rgba(255,255,255,0.3)'}`,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                            cursor: cursorMode === 'select' ? 'move' : 'pointer',
                                            zIndex: 5,
                                            pointerEvents: 'auto'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (cursorMode === 'select') {
                                                // Notify parent about selection via onNoteClick
                                                if (onNoteClick) {
                                                    onNoteClick(e, note);
                                                }
                                            } else if (cursorMode === 'delete') {
                                                // Delete note
                                                const rowKey = isDrums ? `${noteObj.midi}` : noteObj.name;
                                                const gIdx = beatToGridIndex(note.start, gridStepBeats);
                                                toggleNote(rowKey, gIdx);
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            if (cursorMode === 'select') {
                                                e.stopPropagation();
                                                startNoteDrag(e, note.id, note);
                                            }
                                        }}
                                    >
                                        {/* Resize handle on the right edge */}
                                        {cursorMode === 'select' && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: '6px',
                                                    cursor: 'ew-resize',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                    borderRadius: '0 3px 3px 0'
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();

                                                    const startX = e.clientX;
                                                    const originalDuration = note.duration;

                                                    const handleMouseMove = (moveEvent) => {
                                                        moveEvent.preventDefault();

                                                        const dx = moveEvent.clientX - startX;
                                                        const gridUnitsChanged = Math.round(dx / cellWidth);
                                                        const newDurationInGridUnits = Math.max(1, Math.round(originalDuration / gridStepBeats) + gridUnitsChanged);
                                                        const newDuration = newDurationInGridUnits * gridStepBeats;

                                                        if (onNoteClick) {
                                                            const updatedNote = { ...note, duration: newDuration };
                                                            onNoteClick({
                                                                preventDefault: () => { },
                                                                stopPropagation: () => { },
                                                                ctrlKey: true,
                                                                type: 'resize'
                                                            }, updatedNote);
                                                        }
                                                    };

                                                    const handleMouseUp = () => {
                                                        document.removeEventListener('mousemove', handleMouseMove);
                                                        document.removeEventListener('mouseup', handleMouseUp);
                                                    };

                                                    document.addEventListener('mousemove', handleMouseMove);
                                                    document.addEventListener('mouseup', handleMouseUp);
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}

                            {/* Ghost loop notes — read-only, non-interactive copies for each loop repetition */}
                            {clipLoop && sourceDuration > 0 && (() => {
                                const numLoopIters = Math.ceil(clipDuration / sourceDuration);
                                const ghostElements = [];

                                // Source notes: only those within the sourceDuration window
                                const sourceNotes = clipNotes.filter(n =>
                                    (n.start + (n.duration || 0)) > 0 &&
                                    n.start < sourceDuration
                                );

                                for (let iter = 1; iter < numLoopIters; iter++) {
                                    const timeOffset = iter * sourceDuration; // beats
                                    // Don't render notes that start beyond total clip duration
                                    sourceNotes.forEach(note => {
                                        const ghostStart = note.start + timeOffset;
                                        if (ghostStart >= clipDuration) return;

                                        const noteObj = rows.find(r => r.midi === note.pitch);
                                        if (!noteObj) return;

                                        const rowIndex = rows.findIndex(r => r.midi === note.pitch);
                                        const noteLeft = ghostStart * pixelsPerBeat;
                                        const rawDuration = note.duration || gridStepBeats;
                                        const clampedDuration = Math.min(rawDuration, clipDuration - ghostStart);
                                        const noteWidth = Math.max(cellWidth, clampedDuration * pixelsPerBeat);
                                        const noteTop = rowIndex * 24;
                                        const velocity = note.velocity ?? 0.8;
                                        const baseColor = getVelocityColor(velocity, false);

                                        ghostElements.push(
                                            <div
                                                key={`ghost-loop-${iter}-${note.id}`}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${noteLeft}px`,
                                                    top: `${noteTop + 2}px`,
                                                    width: `${noteWidth}px`,
                                                    height: '20px',
                                                    backgroundColor: baseColor,
                                                    borderRadius: '3px',
                                                    border: '1px solid rgba(255,255,255,0.15)',
                                                    opacity: 0.35,
                                                    pointerEvents: 'none',
                                                    zIndex: 4,
                                                    cursor: 'default',
                                                }}
                                            />
                                        );
                                    });
                                }
                                return ghostElements;
                            })()}

                            {/* Grid background - adaptive CSS grid lines based on zoom */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: `${gridWidthPx}px`,
                                    height: `${rows.length * 24}px`,
                                    backgroundImage: adaptiveGridCSS.backgroundImage,
                                    backgroundSize: adaptiveGridCSS.backgroundSize,
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }}
                            />

                            {/* Row backgrounds - render rows but NOT individual cells */}
                            {rows.map((row, noteIndex) => {
                                const isC = !isDrums && row.name.startsWith('C');
                                return (
                                    <div
                                        key={isDrums ? `dr-${row.midi}` : row.name}
                                        data-row-key={isDrums ? `${row.midi}` : row.name}
                                        data-row-index={noteIndex}
                                        style={{
                                            position: 'absolute',
                                            top: `${noteIndex * 24}px`,
                                            left: 0,
                                            width: `${gridWidthPx}px`,
                                            height: '24px',
                                            borderBottom: isC ? '2px solid #444' : '1px solid #2a2a2a',
                                            backgroundColor: isDrums ? '#1a1a1a' : (row.isBlack ? '#0f0f0f' : '#1a1a1a'),
                                            boxSizing: 'border-box',
                                            cursor: cursorMode === 'edit' ? 'pointer' : (cursorMode === 'delete' ? 'pointer' : 'default'),
                                            zIndex: 0
                                        }}
                                        onClick={(e) => {
                                            if (cursorMode === 'edit' || cursorMode === 'delete') {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const x = e.clientX - rect.left;
                                                const beat = x / pixelsPerBeat;
                                                const gridPos = beatToGridIndex(beat, gridStepBeats);
                                                const rowKey = isDrums ? `${row.midi}` : row.name;
                                                const gridCell = grid[`${rowKey}-${gridPos}`] || {};

                                                if (cursorMode === 'edit') {
                                                    toggleNote(rowKey, gridPos);
                                                    if (!gridCell.active) {
                                                        if (isDrums) playDrumPreview(row.midi); else playTestNote(row.name, 0.3);
                                                    }
                                                } else if (cursorMode === 'delete' && (gridCell.active || gridCell.continuation)) {
                                                    toggleNote(rowKey, gridPos);
                                                }
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            if (cursorMode === 'select') {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const x = e.clientX - rect.left;
                                                const beat = x / pixelsPerBeat;
                                                const gridPos = beatToGridIndex(beat, gridStepBeats);
                                                const rowKey = isDrums ? `${row.midi}` : row.name;
                                                const gridCell = grid[`${rowKey}-${gridPos}`] || {};

                                                if (gridCell.active || gridCell.continuation) {
                                                    startNoteDrag(e, gridCell.noteId, gridCell.originalNote);
                                                } else {
                                                    handleSelectionMouseDown(e);
                                                }
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            if (cursorMode === 'edit') {
                                                e.preventDefault();
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const x = e.clientX - rect.left;
                                                const beat = x / pixelsPerBeat;
                                                const gridPos = beatToGridIndex(beat, gridStepBeats);
                                                const rowKey = isDrums ? `${row.midi}` : row.name;
                                                const gridCell = grid[`${rowKey}-${gridPos}`] || {};

                                                toggleNote(rowKey, gridPos);
                                                if (!gridCell.active) {
                                                    if (isDrums) playDrumPreview(row.midi); else playTestNote(row.name, 0.3);
                                                }
                                            }
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PianoRoll;
